import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'

interface Props {
  body: string
  /** Send the comma-joined selection (+ optional freeform addendum) as
   *  the user's next turn. Multiselect uses Submit-to-send so the user
   *  can toggle picks freely before committing. */
  onSend: (text: string) => void
  isLive: boolean
  nextUserContent?: string
}

/**
 * Multi-select card. Replaces the default code-block rendering for
 *
 *     ```concierge-multiselect
 *     - Option A
 *     - Option B
 *     - Option C
 *     ```
 *
 * Same `- option` syntax as `concierge-options`; semantically the user
 * can toggle any subset. Submit sends a comma-joined list. A freeform
 * input is appended after the selected items if filled. Historical
 * selections are recovered by splitting the next user turn on `, ` and
 * matching against the option list.
 */
export function ConciergeMultiSelectCard({
  body,
  onSend,
  isLive,
  nextUserContent,
}: Props) {
  const options = useMemo(
    () =>
      body
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.startsWith('-'))
        .map((l) => l.replace(/^-\s*/, '').trim())
        .filter(Boolean),
    [body]
  )

  // Recover historical answer: split next user turn on `, ` (or commas)
  // and pick any chunk that matches an option label exactly. Anything
  // left over goes into the freeform field.
  const initial = useMemo(() => {
    if (!nextUserContent) return { selected: new Set<string>(), free: '' }
    const chunks = nextUserContent.split(/,\s*/).map((c) => c.trim())
    const sel = new Set<string>()
    const extras: string[] = []
    for (const c of chunks) {
      if (!c) continue
      if (options.includes(c)) sel.add(c)
      else extras.push(c)
    }
    return { selected: sel, free: extras.join(', ') }
  }, [nextUserContent, options])

  const [selected, setSelected] = useState<Set<string>>(initial.selected)
  const [freeform, setFreeform] = useState(initial.free)
  const [submitted, setSubmitted] = useState(
    () => initial.selected.size > 0 || initial.free.length > 0
  )

  if (options.length === 0) {
    return (
      <pre className="concierge-option-card-fallback">
        <code>{body}</code>
      </pre>
    )
  }

  const interactive = isLive && !submitted

  const toggle = (opt: string) => {
    if (!interactive) return
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(opt)) next.delete(opt)
      else next.add(opt)
      return next
    })
  }

  const handleSubmit = () => {
    if (!interactive) return
    const parts: string[] = []
    for (const opt of options) {
      if (selected.has(opt)) parts.push(opt)
    }
    if (freeform.trim()) parts.push(freeform.trim())
    if (!parts.length) return
    setSubmitted(true)
    onSend(parts.join(', '))
  }

  const anySelected = selected.size > 0 || freeform.trim().length > 0

  return (
    <div
      className="concierge-form-card"
      aria-disabled={!interactive}
      data-state={submitted ? 'answered' : isLive ? 'live' : 'frozen'}
    >
      <div className="concierge-option-row">
        {options.map((opt) => {
          const isChosen = selected.has(opt)
          return (
            <button
              key={opt}
              type="button"
              className="concierge-option-btn"
              data-chosen={isChosen ? 'true' : 'false'}
              disabled={!interactive}
              onClick={() => toggle(opt)}
            >
              {isChosen && (
                <Check
                  size={12}
                  strokeWidth={2.5}
                  className="concierge-option-check"
                  aria-hidden
                />
              )}
              <span>{opt}</span>
            </button>
          )
        })}
      </div>
      {interactive && (
        <input
          type="text"
          className="concierge-option-input"
          placeholder="add anything else…"
          value={freeform}
          onChange={(e) => setFreeform(e.target.value)}
        />
      )}
      <div className="concierge-form-actions">
        <button
          type="button"
          className="concierge-form-submit"
          disabled={!interactive || !anySelected}
          onClick={handleSubmit}
        >
          {submitted ? 'Sent' : 'Submit selection'}
        </button>
      </div>
    </div>
  )
}
